import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import { GkCard, GkRowCard } from "@/components/feature/ArticleCards";
import { gkCategories } from "@/mocks/categories";
import { gkArticles } from "@/mocks/news";
import { gkFaqs } from "@/mocks/faqs";
import { gkComments } from "@/mocks/comments";

const SITE = "https://dqxuaad.cn";

export default function Home() {
  return (
    <>
      <Seo
        title="看吃瓜 - 今日新瓜 最新爆料 新鲜瓜速递 每日瓜料准时更新"
        description="看吃瓜每日新鲜瓜，从今日新瓜到最新爆料，每日瓜料准时更新。盯紧全网热点，第一时间为你端上今日值得吃的新瓜，不让你错过任何一场热闹。"
        keywords="看吃瓜,看吃瓜今日新瓜,看吃瓜最新爆料,看吃瓜新鲜瓜速递,看吃瓜每日瓜料,看吃瓜新瓜更新,看吃瓜今日热点"
        canonicalPath="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "看吃瓜",
            url: SITE,
            inLanguage: "zh-CN",
            potentialAction: {
              "@type": "SearchAction",
              target: { "@type": "EntryPoint", urlTemplate: `${SITE}/news?q={search_term_string}` },
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "看吃瓜",
            url: SITE,
            logo: `${SITE}/favicon.ico`,
            description: "每日瓜料准时更新的娱乐资讯门户，提供今日新瓜、最新爆料与新鲜瓜速递。",
          },
        ]}
      />
      <HeroSection />
      <CategoryStrip />
      <LatestFeed />
      <HotRanking />
      <FreshDelivery />
      <CommentsBand />
      <FaqPreview />
    </>
  );
}

function SectionHead({ tag, title, desc, to }: { tag: string; title: string; desc?: string; to?: string }) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        <p className="mb-2 flex items-center gap-2 text-xs font-medium tracking-widest text-primary-600">
          <span className="inline-block h-px w-6 bg-primary-500"></span>{tag}
        </p>
        <h2 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">{title}</h2>
        {desc && <p className="mt-2 max-w-2xl text-sm text-foreground-600">{desc}</p>}
      </div>
      {to && (
        <Link to={to} className="flex items-center gap-1 whitespace-nowrap rounded-full border border-primary-200 px-4 py-2 text-sm text-primary-600 transition-colors hover:bg-primary-50">
          查看全部<i className="ri-arrow-right-line"></i>
        </Link>
      )}
    </div>
  );
}

/* ---------------- Hero ---------------- */
function HeroSection() {
  const featured = gkArticles.filter((a) => a.featured);
  const lead = featured[0];
  const fresh = gkArticles.filter((a) => a.id !== lead.id).slice(0, 5);
  return (
    <section className="mx-auto max-w-7xl px-4 pt-6 md:px-8 md:pt-10">
      <div className="mb-6 flex items-center gap-2 overflow-hidden rounded-full bg-primary-100/70 px-4 py-2 text-xs text-primary-700 md:text-sm">
        <i className="ri-flashlight-fill shrink-0"></i>
        <div className="flex flex-1 items-center gap-2 overflow-hidden">
          <span className="shrink-0 font-semibold whitespace-nowrap">今日热点速递：</span>
          <span className="truncate">{gkArticles[0].title}</span>
        </div>
        <span className="hidden shrink-0 items-center gap-1 sm:flex"><i className="ri-time-line"></i>每日瓜料准时更新</span>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Lead story */}
        <Link to={`/news/${lead.id}`} className="group relative block overflow-hidden rounded-3xl lg:col-span-2">
          <div className="aspect-[16/10] w-full overflow-hidden md:aspect-[16/9]">
            <img src={lead.cover} alt={lead.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent"></div>
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">今日头条瓜</span>
              <span className="rounded-full bg-background-50/90 px-3 py-1 text-xs text-foreground-700">{gkCategories.find((c) => c.slug === lead.category)?.name}</span>
            </div>
            <h1 className="font-heading text-2xl font-bold leading-snug text-background-50 md:text-4xl">{lead.title}</h1>
            <p className="mt-3 hidden max-w-2xl text-sm leading-6 text-background-100/90 md:block">{lead.summary}</p>
            <div className="mt-4 flex items-center gap-4 text-xs text-background-100/80">
              <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{lead.views}</span>
              <span className="flex items-center gap-1"><i className="ri-time-line"></i>{lead.date} {lead.time}</span>
              <span className="flex items-center gap-1"><i className="ri-arrow-right-up-line"></i>立即吃瓜</span>
            </div>
          </div>
        </Link>

        {/* Side fresh stack */}
        <aside className="flex flex-col overflow-hidden rounded-3xl border border-background-200 bg-background-50">
          <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
            <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
              <i className="ri-bard-fill text-primary-500"></i>新鲜瓜速递
            </h3>
            <span className="flex items-center gap-1 text-xs text-primary-600"><span className="h-2 w-2 animate-pulse rounded-full bg-primary-500"></span>实时更新</span>
          </div>
          <div className="flex-1 divide-y divide-background-200">
            {fresh.map((a, i) => (
              <Link key={a.id} to={`/news/${a.id}`} className="group flex gap-3 p-4 transition-colors hover:bg-background-100">
                <span className="font-heading text-xl leading-none text-primary-300">{String(i + 1).padStart(2, "0")}</span>
                <div className="min-w-0">
                  <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 group-hover:text-primary-600">{a.title}</p>
                  <span className="mt-1.5 block text-xs text-foreground-500">{a.date} · {gkCategories.find((c) => c.slug === a.category)?.name}</span>
                </div>
              </Link>
            ))}
          </div>
          <Link to="/news" className="border-t border-background-200 px-5 py-3 text-center text-sm text-primary-600 hover:bg-background-100">更多瓜料 <i className="ri-arrow-right-line"></i></Link>
        </aside>
      </div>
    </section>
  );
}

/* ---------------- Categories ---------------- */
function CategoryStrip() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
      <SectionHead tag="栏目瓜田" title="按口味挑瓜" desc="从明星动态到社交热榜，五大栏目任你挑，今天想先吃哪一口？" />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {gkCategories.map((c, i) => {
          const tone = ["bg-primary-100 text-primary-700", "bg-accent-100 text-accent-800", "bg-secondary-100 text-secondary-800", "bg-accent-100 text-accent-800", "bg-primary-100 text-primary-700"][i % 5];
          return (
            <Link key={c.slug} to={`/category/${c.slug}`} className="group overflow-hidden rounded-2xl border border-background-200 bg-background-50">
              <div className="relative h-28 overflow-hidden">
                <img src={c.image} alt={`${c.name}栏目`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <span className={`absolute bottom-3 left-3 flex h-9 w-9 items-center justify-center rounded-full ${tone}`}><i className={c.icon}></i></span>
              </div>
              <div className="p-4">
                <h3 className="font-heading text-base font-bold text-foreground-900 group-hover:text-primary-600">{c.name}</h3>
                <p className="mt-1 text-xs text-foreground-500">{c.tagline}</p>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/* ---------------- Latest Feed with filter ---------------- */
function LatestFeed() {
  const tabs = [{ slug: "all", name: "全部" }, ...gkCategories.map((c) => ({ slug: c.slug, name: c.name }))];
  const [active, setActive] = useState("all");
  const list = useMemo(() => (active === "all" ? gkArticles : gkArticles.filter((a) => a.category === active)), [active]);
  const shown = list.slice(0, 6);
  return (
    <section className="bg-background-100/60">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
        <SectionHead tag="最新爆料" title="今天的新瓜上桌了" desc="第一手的明星动态、综艺现场与影视热播瓜料，随栏目切换边吃边扫。" />
        <div className="mb-8 flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button
              key={t.slug}
              type="button"
              onClick={() => setActive(t.slug)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${active === t.slug ? "bg-primary-500 text-background-50" : "bg-background-50 text-foreground-700 ring-1 ring-background-200 hover:text-primary-600"}`}
            >
              {t.name}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((a) => (
            <GkCard key={a.id} article={a} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Hot Ranking ---------------- */
function HotRanking() {
  const hot = [...gkArticles].filter((a) => a.hot).slice(0, 6);
  return (
    <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
      <div className="grid gap-8 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <SectionHead tag="全网热度" title="今日吃瓜榜" desc="按热度实时更新的今日热点排行，看看大家此刻都在吃哪些瓜。" />
          <ol className="space-y-3">
            {hot.map((a, i) => (
              <li key={a.id}>
                <Link to={`/news/${a.id}`} className="group flex items-center gap-4 rounded-2xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200">
                  <span className={`font-heading text-3xl font-black ${i < 3 ? "text-primary-500" : "text-background-400"}`}>{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 group-hover:text-primary-600">{a.title}</p>
                    <span className="mt-1 flex items-center gap-1 text-xs text-foreground-500"><i className="ri-eye-line"></i>{a.views} 热度</span>
                  </div>
                </Link>
              </li>
            ))}
          </ol>
        </div>
        <div className="rounded-3xl bg-primary-500 p-6 text-background-50 md:p-8 lg:col-span-3 lg:p-10">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-background-50/15 px-3 py-1 text-xs">吃瓜编辑部 · 热榜速读</span>
            <span className="rounded-full bg-background-50/15 px-3 py-1 text-xs"><i className="ri-fire-line"></i>今日最热瓜</span>
          </div>
          <h3 className="font-heading text-3xl font-bold leading-snug md:text-4xl">{gkArticles[0].title}</h3>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-background-50/90 md:text-base">{gkArticles[0].summary}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <Link to={`/news/${gkArticles[0].id}`} className="rounded-full bg-background-50 px-6 py-2.5 text-sm font-medium whitespace-nowrap text-primary-600 transition-colors hover:bg-background-100">去围观这瓜</Link>
            <span className="flex items-center gap-2 text-xs text-background-50/80"><i className="ri-eye-line"></i>{gkArticles[0].views} 人正在吃</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Fresh delivery editorial ---------------- */
function FreshDelivery() {
  const list = gkArticles.filter((a) => !a.featured).slice(0, 4);
  return (
    <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
      <SectionHead tag="编辑精选" title="值得慢慢嚼的瓜" desc="不止八卦标题，编辑部挑了几条有料又有后续的新鲜瓜料，慢慢展开给你看。" to="/news" />
      <div className="space-y-4">
        {list.map((a) => (
          <GkRowCard key={a.id} article={a} />
        ))}
      </div>
    </section>
  );
}

/* ---------------- Comments ---------------- */
function CommentsBand() {
  return (
    <section className="bg-background-100/60">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
        <SectionHead tag="口碑瓜友" title="他们在吃瓜" desc="每天都有瓜友一起守着更新，看看大家是怎么说看吃瓜的。" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {gkComments.map((c) => (
            <figure key={c.nickname} className="flex h-full flex-col rounded-2xl border border-background-200 bg-background-50 p-5">
              <div className="flex items-center gap-3">
                <span className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold ${c.accent}`}>{c.avatarText}</span>
                <div>
                  <figcaption className="text-sm font-semibold text-foreground-900">{c.nickname}</figcaption>
                  <p className="text-xs text-foreground-500">{c.role}</p>
                </div>
              </div>
              <blockquote className="mt-3 flex-1 text-sm leading-6 text-foreground-700">“{c.text}”</blockquote>
              <p className="mt-3 text-xs text-primary-600">{c.meta}</p>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ preview ---------------- */
function FaqPreview() {
  const [open, setOpen] = useState<number | null>(0);
  const items = gkFaqs.slice(0, 4);
  return (
    <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
      <div className="grid gap-8 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <SectionHead tag="吃瓜百科" title="常见问题" desc="关于看吃瓜的更新节奏、栏目设置与投稿方式，这里都讲明白。" to="/faq" />
        </div>
        <div className="lg:col-span-3">
          <div className="space-y-3">
            {items.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={f.question} className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
                  <button type="button" onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen} className="flex w-full items-center justify-between gap-4 p-4 text-left md:p-5">
                    <h3 className="font-heading text-base font-bold text-foreground-900">{f.question}</h3>
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors ${isOpen ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600"}`}>
                      <i className={`ri-add-line transition-transform ${isOpen ? "rotate-45" : ""}`}></i>
                    </span>
                  </button>
                  {isOpen && <p className="border-t border-background-200 px-4 py-4 text-sm leading-7 text-foreground-600 md:px-5">{f.answer}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}